import React, { useState } from "react";
import { Surface } from "./Surface";
import { HiOutlineCheck, HiOutlineTemplate } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../utils/permissions";
import BusinessCardDisplay from "./BusinessCardDisplay";
import BusinessCardMinimalistDisplay from "./BusinessCardMinimalistDisplay";
import BusinessCardCorporateDisplay from "./BusinessCardCorporateDisplay";
import BusinessCardCreativeDisplay from "./BusinessCardCreativeDisplay";
import BusinessCardTechDisplay from "./BusinessCardTechDisplay";
import BusinessCardVintageDisplay from "./BusinessCardVintageDisplay";
import BusinessCardLuxuryDisplay from "./BusinessCardLuxuryDisplay";
import BusinessCardVibrantDisplay from "./BusinessCardVibrantDisplay";
import BusinessCardMedicalDisplay from "./BusinessCardMedicalDisplay";
import BusinessCardModernDisplay from "./BusinessCardModernDisplay";
import BusinessCardElegantDisplay from "./BusinessCardElegantDisplay";
import BusinessCardBoldDisplay from "./BusinessCardBoldDisplay";
import PreviewBox from "./PreviewBox";
import { BusinessCardTemplate } from "../../types/BusinessCardTypes";
import { UnlockBadge } from "./UnlockBadge";
import UpgradeModal from "../modals/UpgradeModal";

interface BusinessCardTemplateSelectorProps {
  selectedTemplate: BusinessCardTemplate;
  onTemplateChange: (template: BusinessCardTemplate) => void;
  title?: string;
  description?: string;
  showAsCard?: boolean;
  onUpgradeClick?: () => void;
}

const BusinessCardTemplateSelector: React.FC<
  BusinessCardTemplateSelectorProps
> = ({
  selectedTemplate,
  onTemplateChange,
  title,
  description,
  showAsCard = false,
  onUpgradeClick,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const hasAccess = permissions.isAtLeastTier(PlanTier.PREMIUM);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const premiumTemplatesBenefits = [
    {
      id: "multiple-templates",
      text: t("permissions.businessCard.templateSelector.benefit1") as string,
    },
    {
      id: "professional-designs",
      text: t("permissions.businessCard.templateSelector.benefit2") as string,
    },
  ];
  const sampleData = {
    userData: {
      user_identification: "sample-123",
      first_name: "John",
      last_name: "Doe",
      email: "john.doe@company.com",
      phone: "+1 (555) 123-4567",
      avatar:
        "https://cdn.1cdn.app/application/UMD/25090921285476_David_Min.png",
    },
    businessCardData: {
      jobTitle: "Senior Marketing Manager",
      description:
        "Specializing in digital marketing campaigns and customer acquisition strategies.",
      links: [
        {
          id: "1",
          label: "LinkedIn Profile",
          link: "https://linkedin.com/in/johndoe",
          icon: "linkedin",
          icontype: "icon" as const,
        },
        {
          id: "2",
          label: "Portfolio",
          link: "https://johndoe.portfolio.com",
          icon: "website",
          icontype: "icon" as const,
        },
      ],
      uploadedFiles: [],
      allowVCardDownload: true,
    },
    companyLogo: "https://cdn.1cdn.app/application/UMD/2405110558455_logo.png",
    companyName: "Tech Solutions Inc",
    companyDescription: "Innovation through technology",
    entityIdentification: "sample-entity-123",
    ownerIdentification: "sample-owner-123",
  };

  const templates = [
    {
      id: "default" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.default.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.default.description"
      ),
      component: BusinessCardDisplay,
    },
    {
      id: "minimalist" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.minimalist.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.minimalist.description"
      ),
      component: BusinessCardMinimalistDisplay,
    },
    {
      id: "corporate" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.corporate.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.corporate.description"
      ),
      component: BusinessCardCorporateDisplay,
    },
    {
      id: "creative" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.creative.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.creative.description"
      ),
      component: BusinessCardCreativeDisplay,
    },
    {
      id: "tech" as BusinessCardTemplate,
      name: t("permissions.businessCard.templateSelector.templates.tech.name"),
      description: t(
        "permissions.businessCard.templateSelector.templates.tech.description"
      ),
      component: BusinessCardTechDisplay,
    },
    {
      id: "vintage" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.vintage.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.vintage.description"
      ),
      component: BusinessCardVintageDisplay,
    },
    {
      id: "vibrant" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.vibrant.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.vibrant.description"
      ),
      component: BusinessCardVibrantDisplay,
    },
    {
      id: "medical" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.medical.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.medical.description"
      ),
      component: BusinessCardMedicalDisplay,
    },
    {
      id: "luxury" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.luxury.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.luxury.description"
      ),
      component: BusinessCardLuxuryDisplay,
    },
    {
      id: "modern" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.modern.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.modern.description"
      ),
      component: BusinessCardModernDisplay,
    },
    {
      id: "elegant" as BusinessCardTemplate,
      name: t(
        "permissions.businessCard.templateSelector.templates.elegant.name"
      ),
      description: t(
        "permissions.businessCard.templateSelector.templates.elegant.description"
      ),
      component: BusinessCardElegantDisplay,
    },
    {
      id: "bold" as BusinessCardTemplate,
      name: t("permissions.businessCard.templateSelector.templates.bold.name"),
      description: t(
        "permissions.businessCard.templateSelector.templates.bold.description"
      ),
      component: BusinessCardBoldDisplay,
    },
  ];

  const content = (
    <div className={showAsCard ? "p-1 sm:p-4" : ""}>
      {showAsCard && (
        <div className="mb-3 sm:mb-4">
          <h5 className="text-sm sm:text-base font-medium text-gray-900 mb-2">
            {title || t("permissions.businessCard.templateSelector.title")}
          </h5>
          <p className="text-xs sm:text-sm text-gray-600">
            {description ||
              t("permissions.businessCard.templateSelector.description")}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {templates.map((template, index) => {
          const TemplateComponent = template.component;
          const isSelected = selectedTemplate === template.id;
          const isPremiumTemplate = index > 0;
          const isLocked = isPremiumTemplate && !hasAccess;

          const handleClick = () => {
            if (isSelected) {
              return;
            }

            if (isLocked) {
              if (onUpgradeClick) {
                onUpgradeClick();
              } else {
                setShowUpgradeModal(true);
              }
              return;
            }

            onTemplateChange(template.id);
          };

          return (
            <div
              key={template.id}
              className="relative"
              data-testid={`bc-theme-card-${template.id}`}
              data-locked={isLocked ? "true" : "false"}
            >
              <div
                onClick={handleClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                className={`flex w-full items-center gap-3 sm:gap-4 rounded-lg border p-2.5 sm:p-3 text-left transition-all duration-200 ${
                  isLocked ? "cursor-not-allowed" : "cursor-pointer"
                } ${
                  isSelected
                    ? "border-blue-500 bg-blue-50"
                    : isLocked
                    ? "border-gray-200 bg-gray-50"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                <div className="relative w-28 shrink-0 sm:w-32">
                  {isLocked && (
                    <div className="absolute left-1 top-1 z-10">
                      <UnlockBadge />
                    </div>
                  )}
                  <PreviewBox height={180} baseWidth={340} baseHeight={470}>
                    <TemplateComponent
                      {...sampleData}
                      className="pointer-events-none"
                      isPreview
                    />
                  </PreviewBox>
                </div>

                <div className="min-w-0 flex-1">
                  <h6
                    className={`text-sm sm:text-base font-semibold ${
                      isSelected
                        ? "text-blue-900"
                        : isLocked
                        ? "text-gray-600"
                        : "text-gray-900"
                    }`}
                  >
                    {template.name}
                  </h6>
                  <p
                    className={`mt-0.5 text-xs leading-snug ${
                      isSelected
                        ? "text-blue-700"
                        : isLocked
                        ? "text-gray-500"
                        : "text-gray-600"
                    }`}
                  >
                    {template.description}
                  </p>
                </div>

                {isSelected && !isLocked && (
                  <div className="ml-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500">
                    <HiOutlineCheck className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showAsCard && (
        <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-1.5 sm:space-x-2">
            <div className="flex-shrink-0">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full mt-1 sm:mt-1.5"></div>
            </div>
            <div className="text-xs text-blue-700">
              <strong>
                {t("permissions.businessCard.templateSelector.templateApplied")}
                :
              </strong>{" "}
              {t(
                "permissions.businessCard.templateSelector.templateAppliedDescription",
                {
                  templateName: templates.find((t) => t.id === selectedTemplate)
                    ?.name,
                }
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {showAsCard ? <Surface>{content}</Surface> : content}

      <UpgradeModal
        showModal={showUpgradeModal}
        setShowModal={setShowUpgradeModal}
        targetPlan={PlanTier.PREMIUM}
        title={
          t("permissions.businessCard.templateSelector.upgradeTitle") as string
        }
        description={
          t(
            "permissions.businessCard.templateSelector.upgradeDescription"
          ) as string
        }
        benefits={premiumTemplatesBenefits}
        icon={HiOutlineTemplate}
        iconTheme="blue"
        upgradeText={
          t("permissions.businessCard.templateSelector.upgradeText") as string
        }
      />
    </>
  );
};

export default BusinessCardTemplateSelector;
